import React, { useEffect } from 'react';

export default function Lightbox({ src, images = [], onNavigate, zoomLevel, setZoomLevel, onClose }) {
  // Normalize URLs so index matching works even if callers build them differently
  const norm = (u) => (u || '').replace(/^https?:\/\/[^/]+/, '');
  const idx = src ? images.findIndex(i => norm(i) === norm(src)) : -1;
  const hasGallery = idx !== -1 && images.length > 1;
  const goPrev = () => { if (hasGallery && idx > 0 && onNavigate) onNavigate(images[idx - 1]); };
  const goNext = () => { if (hasGallery && idx < images.length - 1 && onNavigate) onNavigate(images[idx + 1]); };

  useEffect(() => {
    if (!src) return;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line
  }, [src, idx, images]);

  if (!src) return null;
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between px-6 py-3 bg-black/50 flex-shrink-0" onClick={e=>e.stopPropagation()}>
        <span className="text-white/70 text-sm">{hasGallery ? `Image ${idx + 1} of ${images.length}` : 'Image preview'}</span>
        <div className="flex items-center gap-3">
          <button onClick={()=>setZoomLevel(z=>Math.max(0.5,z-0.25))} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <span className="text-white text-sm font-mono w-12 text-center">{Math.round(zoomLevel*100)}%</span>
          <button onClick={()=>setZoomLevel(z=>Math.min(4,z+0.25))} className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
          </button>
          <button onClick={()=>setZoomLevel(1)} className="px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-xs font-medium transition">1:1</button>
          <a href={`https://qtalk.qualtechedge.in/api/files/download-image?url=${encodeURIComponent(src)}&name=${encodeURIComponent(src.split('/').pop())}`}
            download target="_blank" rel="noreferrer"
            className="p-2 rounded-xl bg-white/10 hover:bg-teal-500/60 text-white transition flex items-center gap-2 text-xs font-medium px-3"
            onClick={e=>e.stopPropagation()}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download
          </a>
          <button onClick={onClose} className="p-2 rounded-xl bg-white/10 hover:bg-red-500/60 text-white transition">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>
      <div className={`flex-1 overflow-auto p-4 relative ${zoomLevel<=1 ? 'flex items-center justify-center' : ''}`} onClick={e=>e.stopPropagation()}>
        {hasGallery && idx > 0 && (
          <button onClick={goPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition z-10">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
        )}
        <img src={src} alt="preview"
          style={zoomLevel<=1
            ? {maxWidth:'90%',maxHeight:'85vh',borderRadius:'12px',transition:'width 0.15s'}
            : {width:`${zoomLevel*100}%`,maxWidth:'none',maxHeight:'none',borderRadius:'12px',display:'block',margin:'auto',transition:'width 0.15s'}}/>
        {hasGallery && idx < images.length - 1 && (
          <button onClick={goNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition z-10">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        )}
      </div>
      <div className="text-center text-white/30 text-xs pb-3">{hasGallery ? 'Use ← → keys or arrows to browse · Click outside to close' : 'Click outside to close'}</div>
    </div>
  );
}
